import { Box, CircularProgress, Fade, Stack, useTheme } from "@mui/material";
import { PureComponent, useContext, useEffect, useMemo, useRef, useState } from "react";
import { FixedSizeGrid } from "react-window";
import InfiniteLoader from "react-window-infinite-loader";
import AssetCard from "../../Cards/AssetCard/asset-card";
import { handleSelectClickObject, isItemSelectedObject } from "../../../Utils/Helpers/selectMode";
import { IsAssetDownloadable, IsAssetOrderable, IsPermissionGranted } from "../../../Core/Utils/asset-object-utils";
import { PermissionType } from "../../../Core/Api/Types/permission-type";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import { useLocation, useNavigate, useOutletContext } from "react-router";
import { useAppDispatch, useAppSelector } from "../../../Redux/redux-store";
import { authorizedObjectOnPanel } from "./drive-center-panel";
import {
  DrivePanelState,
  GetFilterText,
  GetLayout,
  GetPagination,
  GetPanelObject,
  GetScale,
  SetActiveScale,
  SetPanelObject,
  SetPanelState,
  SetScales,
} from "../../../Redux/Reducers/redux-slice-drive";
import { DriveContext } from "./drive-navigate-handler";
import { DriveGridHeader } from "./drive-grid-header";
import { CHILDREN_PAGE_SIZE } from "../helpers/use-drive-pagination";
import { GridFilterModel, GridPaginationModel, GridSortModel } from "@mui/x-data-grid";

interface IDriveAssetGrid {
  resultObjects: DbAssetObjectInfos[];
  loadContents: (
    paginationModel: GridPaginationModel,
    replace?: boolean,
    filterModel?: GridFilterModel,
    sort?: GridSortModel
  ) => Promise<void>;
}

export const DriveAssetGrid = (props: IDriveAssetGrid) => {
  // Local states, props & ref
  const { resultObjects, loadContents } = props;
  const [shiftPressed, setShiftPressed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [width, setWidth] = useState(1000);
  const [height, setHeight] = useState(1000);
  const containerRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const infiniteLoaderRef = useRef(null);
  const driveContext = useContext(DriveContext);

  // Pagination
  const { loadedChildrenCount, hasNextPage, isNextPageLoading } = useAppSelector(GetPagination);
  const filterText = useAppSelector(GetFilterText);

  const loadMoreItems = (startIndex, stopIndex) => {
    if (isNextPageLoading) {
      return;
    }
    return loadContents({ page: Math.floor(loadedChildrenCount / CHILDREN_PAGE_SIZE), pageSize: CHILDREN_PAGE_SIZE });
  };

  useEffect(() => {
    loadContents({ page: 0, pageSize: CHILDREN_PAGE_SIZE }, true, { items: [], quickFilterValues: [filterText] });
  }, [filterText]);

  // Redux
  const dispatch = useAppDispatch();
  const scale = useAppSelector(GetScale);
  const layout = useAppSelector(GetLayout);
  const panelObject = useAppSelector(GetPanelObject);

  // Context
  const {
    selectedItems: selectedItems,
    setSelectedItems,
    reload,
  } = useOutletContext<{
    setSelectedItems: React.Dispatch<React.SetStateAction<DbAssetObjectInfos[]>>;
    selectedItems: DbAssetObjectInfos[];
    reload: number;
  }>();

  // Local consts & helpers
  const columnCount = useMemo(() => {
    return 12 / scale;
  }, [scale]);

  const handleKeyDown = (e) => {
    if (e.key === "Shift") {
      setShiftPressed(true);
    } else if (shiftPressed) setShiftPressed(false);
  };

  const handleKeyUp = (e) => {
    if (e.key === "Shift") {
      setShiftPressed(false);
    }
  };

  const handleCardClick = (obj: DbAssetObjectInfos) => {
    if (selectedItems.length > 0) {
      if (shiftPressed) {
        // Select all items between selected item and currently clicked video
        const objs = resultObjects;
        let startIndex = resultObjects.findIndex((a) => selectedItems[0].id === a.id);
        let clickedIndex = resultObjects.findIndex((a) => obj.id === a.id);

        if (startIndex > clickedIndex) {
          //we're going backwards: means we need to recalculate things up with last selectedItems
          startIndex = resultObjects.findIndex((a) => selectedItems[selectedItems.length - 1].id === a.id);
        }

        setSelectedItems(() => {
          if (startIndex < clickedIndex) return objs.slice(startIndex, clickedIndex + 1);
          else return objs.slice(clickedIndex, startIndex + 1);
        });
      } else {
        setSelectedItems((x) =>
          x.findIndex((a) => a.id === obj.id) !== -1 ? [...x.filter((a) => a.id !== obj.id)] : [...x, obj]
        );
      }
      return;
    }

    if (authorizedObjectOnPanel.includes(obj.type)) {
      dispatch(SetPanelObject(obj));
      dispatch(SetPanelState(DrivePanelState.Preview));
    }
  };

  const handleCardDoubleClick = (obj: DbAssetObjectInfos) => {
    const isFolder = obj.type === ObjectType.Folder || obj.type === ObjectType.Group;

    if (isFolder) {
      navigate(driveContext?.basePath + "/" + obj.id, {
        state: {
          back: {
            retrieveId: obj.id,
            path: location.pathname,
            back: location.state?.back,
            layout: layout,
          },
        },
      });
      return;
    }

    if (authorizedObjectOnPanel.includes(obj.type) && selectedItems.length === 0) {
      dispatch(SetPanelObject(obj));
      dispatch(SetPanelState(DrivePanelState.Enlarged));
    }
  };

  const handleOpenPreviewPanel = (obj: DbAssetObjectInfos) => {
    dispatch(SetPanelObject(obj));
    dispatch(SetPanelState(DrivePanelState.Preview));
  };

  // Effects;
  useEffect(() => {
    const resizeObserver = new ResizeObserver((event) => {
      event[0].contentBoxSize[0].inlineSize && setWidth(event[0].contentBoxSize[0].inlineSize);
      event[0].contentBoxSize[0].blockSize && setHeight(event[0].contentBoxSize[0].blockSize);

      // recalculate scales to avoid very small asset cards
      // 6 colonnes = > 1000
      // 4 colonnes = >650
      // 3 colonnes = >500
      // 2 colonnes = >340
      if (event[0].contentBoxSize[0].inlineSize < 340) {
        if (scale === 2 || scale === 3 || scale === 4 || scale === 6) dispatch(SetActiveScale(12));
        dispatch(SetScales([12]));
      } else if (event[0].contentBoxSize[0].inlineSize < 500) {
        if (scale === 2 || scale === 3 || scale === 4) dispatch(SetActiveScale(6));
        dispatch(SetScales([6, 12]));
      } else if (event[0].contentBoxSize[0].inlineSize < 650) {
        if (scale === 2 || scale === 3) dispatch(SetActiveScale(4));
        dispatch(SetScales([4, 6, 12]));
      } else if (event[0].contentBoxSize[0].inlineSize < 1000) {
        if (scale === 2) dispatch(SetActiveScale(3));
        dispatch(SetScales([3, 4, 6, 12]));
      } else {
        dispatch(SetScales([2, 3, 4, 6, 12]));
      }
    });

    if (containerRef.current) resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Remove cached items on mount
  useEffect(() => {
    // We only need to reset cached items when "sortOrder" changes.
    // This effect will run on mount too; there's no need to reset in that case.
    if (infiniteLoaderRef.current) {
      //@ts-expect-error
      infiniteLoaderRef.current.resetloadMoreItemsCache();
    }
  }, [resultObjects]);

  return (
    <Stack>
      <DriveGridHeader />
      <Box
        sx={(t) => ({ width: "100%", height: "100%", pr: 2 })}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        ref={containerRef}
      >
        <InfiniteLoader
          ref={infiniteLoaderRef}
          loadMoreItems={loadMoreItems}
          itemCount={hasNextPage ? loadedChildrenCount + 1 : loadedChildrenCount}
          isItemLoaded={(index) => index < loadedChildrenCount}
        >
          {({ onItemsRendered, ref }) => (
            <FixedSizeGrid
              ref={ref}
              columnCount={columnCount}
              columnWidth={(width - GUTTER_SIZE) / columnCount}
              height={height}
              rowCount={
                resultObjects.length % columnCount === 0
                  ? resultObjects.length / columnCount + 1
                  : (resultObjects.length + columnCount) / columnCount + 1
              }
              rowHeight={width / columnCount / (16 / 9) + 56 + GUTTER_SIZE}
              width={width}
              overscanRowCount={3}
              style={{
                ...theme.helpers(theme).scrollBar,
              }}
              itemData={{
                assets: resultObjects,
                scale,
                columnCount,
                selectedItems,
                panelObject,
                setSelectedItems,
                handleOpenPreviewPanel,
                onCardClick: handleCardClick,
                onCardDoubleClick: handleCardDoubleClick,
                onOpenPanel: handleOpenPreviewPanel,
                loadingNextPageIndex: hasNextPage ? loadedChildrenCount : -1,
                isItemLoaded: (index) => !hasNextPage || index < loadedChildrenCount,
              }}
              onItemsRendered={(gridProps) => {
                onItemsRendered({
                  overscanStartIndex: gridProps.overscanRowStartIndex * columnCount,
                  overscanStopIndex: gridProps.overscanRowStopIndex * columnCount,
                  visibleStartIndex: gridProps.visibleRowStartIndex * columnCount,
                  visibleStopIndex: gridProps.visibleRowStopIndex * columnCount,
                });
              }}
            >
              {AssetCardRenderer}
            </FixedSizeGrid>
          )}
        </InfiniteLoader>
      </Box>
    </Stack>
  );
};

export const GUTTER_SIZE = 16;

class AssetCardRenderer extends PureComponent<any> {
  render() {
    const { columnIndex, data, rowIndex, style } = this.props as any;

    // Access the data source using the "data" prop:
    const {
      assets,
      scale,
      selectedItems,
      panelObject,
      setSelectedItems,
      openPreviewPanel,
      onCardClick,
      onCardDoubleClick,
      loadingNextPageIndex,
      isItemLoaded,
    } = data;
    const index = columnIndex + rowIndex * (12 / scale);

    if (index === loadingNextPageIndex)
      return (
        <div
          style={{
            top: style.top,
            height: style.height - 2 * GUTTER_SIZE,
            left: style.left + GUTTER_SIZE,
            position: style.position,
            width: style.width - 2 * GUTTER_SIZE,
            transition: "width .1s ease-in-out",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
          className="assetGridSingle"
          key={`grid-loader-${index}`}
        >
          <CircularProgress size={36} />
        </div>
      );

    const obj = assets[index];

    if (!obj) return <></>;

    return (
      <Fade in={isItemLoaded(index)} timeout={500}>
        <div
          style={{
            top: style.top,
            height: style.height,
            left: style.left,
            position: style.position,
            width: style.width,
            //   transition: "width .1s ease-in-out",
            paddingLeft: GUTTER_SIZE,
            paddingTop: GUTTER_SIZE,
          }}
          className="assetGridSingle"
          key={`grid-card-${index}`}
        >
          <AssetCard
            draggable
            selectedItems={selectedItems}
            key={obj.id}
            labelList={[obj.type]}
            selectMode={obj.id !== 500}
            selected={isItemSelectedObject(obj, selectedItems)}
            active={panelObject?.id === obj.id}
            onSelectClick={handleSelectClickObject(selectedItems, setSelectedItems)}
            onCardClick={onCardClick}
            onCardDoubleClick={onCardDoubleClick}
            onPanelOpen={() => openPreviewPanel(obj)}
            scale={scale}
            shareBtn={IsPermissionGranted(obj, PermissionType.Share)}
            orderBtn={IsAssetOrderable(obj)}
            renameBtn={IsPermissionGranted(obj, PermissionType.MetadataEdit)}
            trashBtn={IsPermissionGranted(obj, PermissionType.Trash)}
            infoBtn={true}
            downloadBtn={IsAssetDownloadable(obj)}
            onClickInfo={() => {
              if (
                obj.type === ObjectType.Video ||
                obj.type === ObjectType.Audio ||
                obj.type === ObjectType.Document ||
                obj.type === ObjectType.Subtitle ||
                obj.type === ObjectType.Image
              ) {
                openPreviewPanel(obj);
              }
            }}
            showDriveActions={true}
            obj={obj}
          />
        </div>
      </Fade>
    );
  }
}
